var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/** Registration agencies a publisher can be registered with. */
export var REGISTRATION_AGENCIES = ["ISBN", "ISMN"];
/**
 * Returns registrations that are currently active (not terminated).
 */
export var getActiveRegistrations = function (publisher) {
    var _a;
    return ((_a = publisher === null || publisher === void 0 ? void 0 : publisher.registrations) !== null && _a !== void 0 ? _a : []).filter(function (registration) { return !registration.terminationTime; });
};
/**
 * Returns true when the publisher has an active (not terminated)
 * registration at the given agency.
 */
export var hasActiveRegistrationAtAgency = function (publisher, agency) {
    return getActiveRegistrations(publisher).some(function (registration) { return registration.registrationAgency === agency; });
};
/**
 * Returns the agencies the publisher can still be registered with, i.e. those
 * without a currently active registration.
 */
export var getAvailableAgencies = function (publisher) {
    return REGISTRATION_AGENCIES.filter(function (agency) { return !hasActiveRegistrationAtAgency(publisher, agency); });
};
/**
 * Returns true when the publisher has an active ONE_OFF registration.
 */
export var hasActiveOneOffRegistration = function (publisher) {
    return getActiveRegistrations(publisher).some(function (registration) { return registration.registrationIntent === "ONE_OFF"; });
};
/**
 * Returns true when the publisher has an active registration that is not
 * ONE_OFF (continuous publishing). Only such a registration gets the
 * publisher's own prefix assigned on approval — a one-off registration always
 * gets a number from the collective prefix.
 */
export var hasActiveNonOneOffRegistration = function (publisher) {
    return getActiveRegistrations(publisher).some(function (registration) { return registration.registrationIntent !== "ONE_OFF"; });
};
/**
 * Returns true when the publisher has a ONE_OFF registration that a restore
 * (CLOSED/CLOSING -> ACTIVE) would reactivate — its termination time equals
 * the publisher's close time.
 */
export var hasOneOffRegistrationReactivatedByRestore = function (publisher) {
    var _a;
    return !!(publisher === null || publisher === void 0 ? void 0 : publisher.closeTime) &&
        ((_a = publisher.registrations) !== null && _a !== void 0 ? _a : []).some(function (registration) {
            return registration.registrationIntent === "ONE_OFF" &&
                registration.terminationTime === publisher.closeTime;
        });
};
/**
 * Returns true when the edit form shows the one-off work specification (book)
 * section: the publisher has an active ONE_OFF registration, or the restore form
 * reactivates one — unless the restore converts it to continuous publishing.
 */
export var shouldShowWorkSpecSection = function (publisher, status, restoreAsContinuous) {
    return hasActiveOneOffRegistration(publisher) ||
        (status === "RESTORE" &&
            !restoreAsContinuous &&
            hasOneOffRegistrationReactivatedByRestore(publisher));
};
/**
 * Formats registrations into a short summary,
 * e.g. "ISBN – soustavná, ISMN – jednorázová".
 */
export var formatRegistrationsSummary = function (formatMessage, registrations) {
    if (registrations === void 0) { registrations = []; }
    return registrations
        .map(function (registration) {
        return "".concat(registration.registrationAgency, " \u2013 ").concat(formatMessage({
            id: "ui-br-publishers.registration.summary.".concat(registration.registrationIntent.toLowerCase()),
        }));
    })
        .join(", ");
};
/**
 * Returns registrations sorted with active ones first,
 * then by registration time (newest first).
 */
export var sortRegistrationsActiveFirst = function (registrations) {
    if (registrations === void 0) { registrations = []; }
    return __spreadArray([], registrations, true).sort(function (a, b) {
        var _a, _b;
        var aActive = a.terminationTime ? 1 : 0;
        var bActive = b.terminationTime ? 1 : 0;
        if (aActive !== bActive)
            return aActive - bActive;
        return ((_a = b.registrationTime) !== null && _a !== void 0 ? _a : "").localeCompare((_b = a.registrationTime) !== null && _b !== void 0 ? _b : "");
    });
};
/**
 * Decides whether the registration can be converted to CONTINUOUS and whether the confirmation offers the
 * agency-only "without prefix assignment" option (DB-898). The agency keeps its behaviour in every view; the
 * publisher admin converts only in "Můj nakladatel" and the agency then always assigns the prefix. The backend
 * enforces the same rules (own publisher only, publisher admin only, the option ignored for a publisher caller).
 *
 * @param registration the registration shown in the section
 * @param permissions the user's permission flags
 * @param ownPublisher true when the detail is the user's own publisher ("Můj nakladatel")
 * @returns the conversion rights for the registration
 */
export var getRegistrationConversionRights = function (registration, permissions, ownPublisher) {
    var isActiveOneOff = !registration.terminationTime &&
        registration.registrationIntent === "ONE_OFF";
    var isAgency = permissions.canManagePublisherRegistrations;
    var isOwnPublisherAdmin = ownPublisher && permissions.canConvertOwnPublisherRegistration;
    return {
        canConvert: isActiveOneOff && (isAgency || isOwnPublisherAdmin),
        canChooseWithoutPrefixAssignment: isAgency,
    };
};
